/**
 * Map persisted request node types onto the real Vue components.
 * The page still stores `{ type: 'mindset', tags }` — titles and rows
 * resolve at render.
 */

import { NODE_TYPE_ALIAS_MAP } from './pageBuilderContract';
import { describeRequestTile } from './nodes/requestTileModel';

function firstSection(tags) {
  const list = tags || [];
  return (
    ['foundation', 'retain', 'train', 'onboard', 'recruit'].find((tag) =>
      list.includes(tag)
    ) || ''
  );
}

/**
 * Chat heading / text / title props become titled markdownDoc fields.
 * heading/title with only text → title; heading with title+text → titled starter;
 * text → starter body.
 */
export function titledMarkdownDocProps(type, props = {}) {
  const title = String(props.title || '').trim();
  const text = String(props.text || '').trim();
  const seed = String(props.starter || props.md || '').trim();
  return {
    title: title || (type === 'text' ? '' : text),
    starter: seed || (type === 'text' || title ? text : ''),
    icon: props.icon || '',
  };
}

export function persistRequestNode(node) {
  const type = node?.nodeType || '';
  if (type === 'heading' || type === 'title' || type === 'text') {
    return {
      nodeType: 'markdownDoc',
      props: titledMarkdownDocProps(type, node?.props || {}),
    };
  }
  return {
    nodeType: type,
    props: node?.props,
  };
}

export function resolveRequestNode(node) {
  const type = node?.nodeType || '';
  const props = { ...(node?.props || {}) };

  if (type === 'mindset') {
    const tags = props.tags || [];
    const section = firstSection(tags);
    return {
      nodeType: 'videoGallery',
      props: {
        title:
          props.title && props.title !== 'Mindset'
            ? props.title
            : section
            ? `How to think about ${section}`
            : 'Mindset',
        description: props.description || '',
        icon: props.icon || 'mdi-lightbulb-outline',
        tags,
        videos: props.videos?.length ? props.videos : [],
        activeId: props.activeId || '',
      },
    };
  }

  if (type === 'stories') {
    return {
      nodeType: 'stories',
      props: {
        tags: props.tags || [],
        items: props.items?.length ? props.items : [],
      },
    };
  }

  if (type === 'guides') {
    return {
      nodeType: 'guide',
      props: {
        title: props.title || 'Guides',
        icon: props.icon,
        tags: props.tags || [],
      },
    };
  }

  if (type === 'news') {
    return {
      nodeType: 'whatsNew',
      props: {
        title: props.title || "What's New",
        icon: props.icon,
        tags: props.tags || [],
        articles: props.articles || [],
      },
    };
  }

  if (type === 'actions') {
    const items = Array.isArray(props.items) ? props.items : [];
    return {
      nodeType: 'askCy',
      props: {
        title: props.title || 'Ask CY',
        icon: props.icon,
        prompts: items.filter((item) => item && item.kind !== 'link'),
        links: items.filter((item) => item && item.kind === 'link'),
      },
    };
  }

  if (type === 'promptBox') {
    return {
      nodeType: 'askCy',
      props: {
        title: props.title || 'Ask CY',
        icon: props.icon,
        variant: 'box',
        placeholder: props.placeholder || 'Ask CY what to do next…',
        prompts: Array.isArray(props.prompts) ? props.prompts : [],
        links: Array.isArray(props.links) ? props.links : [],
      },
    };
  }

  if (type === 'markdown') {
    return {
      nodeType: 'markdownDoc',
      props: {
        title: props.title || '',
        starter: props.starter || '',
      },
    };
  }

  if (type === 'callout') {
    return {
      nodeType: 'markdownDoc',
      props: {
        title: '',
        starter: props.md || '',
        icon: props.icon || '',
      },
    };
  }

  if (type === 'heading' || type === 'title' || type === 'text') {
    return persistRequestNode({ nodeType: type, props });
  }

  if (type === 'dataList') {
    const { key, ...declaredProps } = props;
    return {
      nodeType: type,
      props: {
        ...declaredProps,
        requestKey: props.requestKey || key || props.request || '',
      },
    };
  }

  if (NODE_TYPE_ALIAS_MAP[type] === 'requestTile') {
    const requestKey =
      props.key || props.kind || props.phase || props.object || '';
    const listItems = Array.isArray(props.items) ? props.items : [];
    const itemCount =
      typeof props.items === 'number' ? props.items : props.itemCount || null;
    const described = describeRequestTile({
      requestType: type,
      requestKey,
      title: props.title || '',
      phase: props.phase || '',
    });
    return {
      nodeType: 'requestTile',
      props: {
        requestType: type,
        requestKey,
        title: described.title,
        items: listItems,
        emptyPrompt: props.emptyPrompt,
        phase: props.phase || '',
        minutes: props.minutes || null,
        itemCount,
        report: props.report || '',
        saved: props.saved || '',
        params: props.params || {},
        prereq: props.prereq || '',
        label: props.label || described.title,
        shape: props.shape || '',
      },
    };
  }

  return { nodeType: NODE_TYPE_ALIAS_MAP[type] || type, props };
}
